import React, { useCallback, useMemo } from "react";
import { Column, Row } from "@/shared/ui/Blocks";
import { AvatarWithInitials } from "@/shared/ui/Avatar";
import { cn } from "@/shared/utils/shadcn";
// @ts-ignore
import ProfileSummaryIllustration from "@/images/backgrounds/profile-summary.svg?react";
import { ContactsRow } from "@/shared/widgets/ContactsRow";

interface HeaderTag {
  color?: string;
  title?: string;
  info?: string;
}

interface ProfileHeaderProps {
  avatarUrl?: string;
  name?: string;
  initials?: string;
  teamName?: string;
  positionName?: string;
  RightComponent?: React.ReactNode;
  tags?: HeaderTag[];
  className?: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  canEdit?: boolean;
  isContact?: boolean;
  onContactChange?: ({
    email,
    phone,
    linkedin,
  }: {
    email?: string;
    phone?: string;
    linkedin?: string;
  }) => void;
}

function ProfileHeader(props: ProfileHeaderProps) {
  const {
    avatarUrl,
    initials,
    name,
    teamName,
    positionName,
    RightComponent,
    className,
    email,
    phone,
    linkedin,
    onContactChange,
    canEdit,
    isContact,
  } = props;

  const PositionNameRow = useCallback(
    () => (
      <Column className="flex w-full md:items-center md:gap-2 md:flex-row">
        {teamName && (
          <Column>
            <span className="text-sm text-gray-700 font-semibold">
              {teamName}
            </span>
          </Column>
        )}
        {teamName && positionName && <span className="hidden md:block">/</span>}
        {positionName && (
          <Column>
            <span className="text-sm font-medium text-gray-500">
              {positionName}
            </span>
          </Column>
        )}
      </Column>
    ),
    [teamName, positionName]
  );

  return (
    <Column>
      <div
        className={cn(
          "relative h-[200px] print:h-[150px] border-y-grey-200 border-solid border-y flex-wrap print:border-grey-100 overflow-hidden  bg-linear-to-r from-white to-blue-100",
          className
        )}
      >
        <Row className="absolute w-full items-center h-full gap-8 flex-1 py-4 pr-4">
          <Row className="items-center h-full gap-8 flex-1 py-4 pr-4">
            <AvatarWithInitials
              initials={initials || name?.charAt(0)}
              avatar={avatarUrl}
              size="2xl"
            />
            <Column>
              <h2>{name}</h2>
              <PositionNameRow />
            </Column>
          </Row>
          {RightComponent}
        </Row>
        <div className="flex items-center justify-end z-0 pointer-events-none h-full w-full">
          <ProfileSummaryIllustration className="h-full w-auto max-w-[320px] text-white" />
        </div>
      </div>
      <ContactsRow
        email={email}
        phone={phone}
        linkedin={linkedin}
        onContactChange={onContactChange}
        editable={canEdit}
        isContact={isContact}
      />
    </Column>
  );
}

export default ProfileHeader;
